import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useSeo } from "../../hooks/useSeo";
import { ARTICLES, type Article } from "../../mocks/mockContent";
import { formatCount } from "../../utils/format";

function ReadingBar() {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const el = document.documentElement;
      const max = el.scrollHeight - window.innerHeight;
      setPct(max > 0 ? Math.min(100, (window.scrollY / max) * 100) : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div className="fixed inset-x-0 top-16 z-30 h-1 bg-transparent">
      <div className="h-full bg-primary-500 transition-[width] duration-150" style={{ width: `${pct}%` }} />
    </div>
  );
}

function RelatedCard({ a }: { a: Article }) {
  return (
    <Link to={`/read/${a.id}`} className="group flex flex-col overflow-hidden rounded-2xl border hairline bg-background-50 transition-colors hover:border-primary-300">
      <img src={a.cover} alt={a.title} className="h-36 w-full object-cover object-top" />
      <div className="p-4">
        <span className="text-xs font-semibold text-accent-700">{a.category}</span>
        <h3 className="mt-2 line-clamp-2 font-display text-base font-semibold leading-snug text-foreground-950 group-hover:text-primary-700">
          {a.title}
        </h3>
        <p className="mt-2 flex items-center gap-2 text-xs text-foreground-500">
          <i className="ri-eye-line" aria-hidden="true"></i>
          {formatCount(a.read)}
        </p>
      </div>
    </Link>
  );
}

export default function ReadPage() {
  const { id } = useParams();
  const num = Number(id);
  const article = useMemo(() => ARTICLES.find((a) => a.id === num), [num]);

  const related = useMemo(() => {
    if (!article) return [];
    const same = ARTICLES.filter((a) => a.id !== article.id && a.category === article.category);
    const others = ARTICLES.filter((a) => a.id !== article.id && a.category !== article.category);
    return [...same, ...others].slice(0, 4);
  }, [article]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [id]);

  useSeo({
    title: article ? `${article.title} - ${article.category}` : "内容不存在",
    description: article ? article.summary : "抱歉，你要查找的内容不存在或已被移除。",
    path: `/read/${num}`,
    type: "article",
    ld: article
      ? {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: article.title,
          description: article.summary,
          image: article.cover,
          author: { "@type": "Organization", name: article.author },
          publisher: { "@type": "Organization", name: "瓜田日报" },
          datePublished: `2026-${article.date.replace(" ", "T")}:00+08:00`,
          inLanguage: "zh-CN",
          mainEntityOfPage: `https://uyizhan.cn/read/${article.id}`,
        }
      : null,
  });

  if (!article) {
    return (
      <div className="flex flex-col items-center justify-center bg-background-50 px-4 py-28 text-center">
        <i className="ri-error-warning-line text-6xl text-background-300" aria-hidden="true"></i>
        <h1 className="mt-5 font-display text-2xl font-bold text-foreground-950">这篇瓜被吃没了</h1>
        <p className="mt-3 text-foreground-600">内容可能已被移除或链接有误，去瓜田逛逛别的吧。</p>
        <Link to="/rexun" className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 hover:bg-primary-600">
          回到热闻列表
        </Link>
      </div>
    );
  }

  return (
    <>
      <ReadingBar />
      <article className="bg-background-50">
        <div className="mx-auto max-w-3xl px-4 py-10 md:px-6 md:py-14">
          {/* breadcrumb */}
          <nav aria-label="面包屑" className="text-sm text-foreground-500">
            <ol className="flex flex-wrap items-center gap-2">
              <li><Link to="/" className="hover:text-primary-700">首页</Link></li>
              <li aria-hidden="true"><i className="ri-arrow-right-s-line"></i></li>
              <li><Link to="/rexun" className="hover:text-primary-700">吃瓜热闻</Link></li>
              <li aria-hidden="true"><i className="ri-arrow-right-s-line"></i></li>
              <li><Link to={`/rexun?cat=${encodeURIComponent(article.category)}`} className="hover:text-primary-700">{article.category}</Link></li>
            </ol>
          </nav>

          <header className="mt-6">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-background-50">
              <i className="ri-quill-pen-line" aria-hidden="true"></i>
              {article.category}
            </span>
            <h1 className="mt-4 font-display text-2xl font-bold leading-snug text-foreground-950 md:text-4xl md:leading-[1.2]">
              {article.title}
            </h1>
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-500">
              <span className="inline-flex items-center gap-1.5 font-medium text-foreground-700">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-500 text-background-50">
                  <i className="ri-edit-2-line text-xs" aria-hidden="true"></i>
                </span>
                {article.author}
              </span>
              <span>{article.date}</span>
              <span className="inline-flex items-center gap-1"><i className="ri-eye-line" aria-hidden="true"></i>{formatCount(article.read)} 阅读</span>
              <span className="inline-flex items-center gap-1"><i className="ri-heart-3-line" aria-hidden="true"></i>{formatCount(article.likes)}</span>
            </div>
          </header>

          <figure className="mt-8 overflow-hidden rounded-2xl">
            <img src={article.cover} alt={article.title} className="h-72 w-full object-cover object-top md:h-[420px]" />
          </figure>

          <p className="mt-8 rounded-2xl border-l-4 border-primary-500 bg-background-100/60 p-5 font-display text-lg font-medium leading-8 text-foreground-800">
            {article.summary}
          </p>

          <div className="mt-6 space-y-5">
            {article.body.map((p, i) => (
              <p key={i} className="text-[15px] leading-8 text-foreground-800">
                {p}
              </p>
            ))}
          </div>

          <footer className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t hairline pt-6">
            <div className="flex flex-wrap gap-2">
              {article.tags.map((t) => (
                <Link key={t} to={`/rexun?q=${encodeURIComponent(t)}`} className="whitespace-nowrap rounded-full border border-background-300 bg-background-100 px-3 py-1 text-xs text-foreground-600 transition-colors hover:border-primary-400 hover:text-primary-700">
                  #{t}
                </Link>
              ))}
            </div>
            <div className="flex items-center gap-2 text-foreground-500">
              <span className="text-sm">分享到</span>
              <a href="https://www.example.com" target="_blank" rel="nofollow noopener" aria-label="分享到微博" className="flex h-8 w-8 items-center justify-center rounded-full bg-background-100 hover:bg-accent-100 hover:text-accent-700"><i className="ri-weibo-fill" aria-hidden="true"></i></a>
              <a href="https://www.example.com" target="_blank" rel="nofollow noopener" aria-label="分享到微信" className="flex h-8 w-8 items-center justify-center rounded-full bg-background-100 hover:bg-accent-100 hover:text-accent-700"><i className="ri-wechat-fill" aria-hidden="true"></i></a>
            </div>
          </footer>

          {/* author box */}
          <aside className="mt-8 flex items-center gap-4 rounded-2xl border hairline bg-background-100/50 p-5">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary-500 text-xl text-background-50">
              <i className="ri-flower-line" aria-hidden="true"></i>
            </span>
            <div className="min-w-0">
              <p className="font-display text-base font-semibold text-foreground-900">瓜田日报编辑部</p>
              <p className="mt-1 text-sm leading-6 text-foreground-600">
                坚持「先核实再讨论」的健康吃瓜原则，每日为读者聚合有据可查、观点正向的娱乐与公共话题内容。
              </p>
            </div>
            <Link to="/about#contact" className="ml-auto hidden shrink-0 whitespace-nowrap rounded-full bg-secondary-600 px-4 py-2 text-sm font-medium text-background-50 hover:bg-secondary-700 sm:inline-flex">
              投稿交流
            </Link>
          </aside>
        </div>
      </article>

      {/* related */}
      <section className="border-t hairline bg-background-100/60" aria-label="相关推荐">
        <div className="mx-auto max-w-6xl px-4 py-12 md:px-6">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-2xl font-bold text-foreground-950">相关推荐</h2>
            <Link to="/rexun" className="text-sm text-foreground-600 transition-colors hover:text-primary-700">
              更多瓜田 <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {related.map((a) => (
              <RelatedCard key={a.id} a={a} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}